import { cn } from '@quadralize/utils'
import * as React from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '../../overlays/popover/popover'
import { Calendar } from '../calendar/calendar'
import { SCHEDULER_VIEW_LABELS, SCHEDULER_VIEW_OPTIONS } from './constants'
import type { SchedulerDensity, SchedulerView } from './types'
import { startOfDay } from './utils'

interface SchedulerDateTitleProps {
  label: string
  selectedDate: Date
  view: SchedulerView
  showDatePicker: boolean
  datePickerLabel: string
  triggerClassName: string
  textClassName: string
  onDateChange: (date: Date) => void
  onDatePick?: (date: Date, view: SchedulerView) => void
}

export const SchedulerDateTitle = ({
  label,
  selectedDate,
  view,
  showDatePicker,
  datePickerLabel,
  triggerClassName,
  textClassName,
  onDateChange,
  onDatePick,
}: SchedulerDateTitleProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = React.useState(false)

  return showDatePicker ? (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={datePickerLabel} className={triggerClassName}>
          <span className="truncate">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => {
            if (!date) return
            const normalizedDate = startOfDay(date)
            onDateChange(normalizedDate)
            onDatePick?.(normalizedDate, view)
            setIsOpen(false)
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  ) : (
    <p className={textClassName}>{label}</p>
  )
}

interface SchedulerHeaderControlsProps {
  view: SchedulerView
  density?: SchedulerDensity
  showViewSwitcher?: boolean
  showTodayButton?: boolean
  todayButtonLabel: string
  isCurrentPeriod: boolean
  onTodayClick: () => void
  onViewChange?: (view: SchedulerView) => void
  containerClassName?: string
  wrapperClassName?: string
  buttonClassName?: string
  activeClassName?: string
  inactiveClassName?: string
  todayAriaLabel?: string
}

const getDefaultContainerClass = (density: SchedulerDensity): string =>
  cn(
    'inline-flex items-center gap-1 rounded-xl border border-border-default/80 bg-bg-base/90 shadow-sm supports-[backdrop-filter]:backdrop-blur',
    density === 'compact' ? 'p-0.5' : 'p-1',
  )

const getDefaultButtonClass = (density: SchedulerDensity): string =>
  cn(
    'rounded-lg font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-base',
    density === 'compact' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]',
  )

const DEFAULT_ACTIVE_CLASS = 'bg-primary/12 text-primary shadow-sm'
const DEFAULT_INACTIVE_CLASS = 'text-text-tertiary hover:bg-bg-muted/80 hover:text-text-primary'

export const SchedulerHeaderControls = ({
  view,
  density = 'comfortable',
  showViewSwitcher = false,
  showTodayButton = false,
  todayButtonLabel,
  isCurrentPeriod,
  onTodayClick,
  onViewChange,
  containerClassName,
  wrapperClassName,
  buttonClassName,
  activeClassName = DEFAULT_ACTIVE_CLASS,
  inactiveClassName = DEFAULT_INACTIVE_CLASS,
  todayAriaLabel = 'Voltar para hoje',
}: SchedulerHeaderControlsProps): React.JSX.Element | null => {
  if (!showViewSwitcher && !showTodayButton) return null

  const resolvedContainerClass = containerClassName ?? getDefaultContainerClass(density)
  const resolvedButtonClass = buttonClassName ?? getDefaultButtonClass(density)

  return (
    <div className={cn('inline-flex items-center gap-2', wrapperClassName)}>
      {showTodayButton && (
        <button
          type="button"
          onClick={onTodayClick}
          disabled={isCurrentPeriod}
          aria-current={isCurrentPeriod ? 'date' : undefined}
          aria-label={todayAriaLabel}
          className={cn(
            'rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
            density === 'compact' ? 'px-2.5 py-0.5 text-[10px]' : 'px-3 py-1 text-[11px]',
            isCurrentPeriod
              ? 'border border-primary/25 bg-primary/8 text-primary cursor-default'
              : 'border border-border-default text-text-secondary cursor-pointer hover:bg-bg-muted hover:text-text-primary',
          )}
        >
          {todayButtonLabel}
        </button>
      )}

      {showViewSwitcher && (
        <div className={resolvedContainerClass}>
          {SCHEDULER_VIEW_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onViewChange?.(option)}
              aria-pressed={option === view}
              className={cn(
                resolvedButtonClass,
                option === view ? activeClassName : inactiveClassName,
              )}
            >
              {SCHEDULER_VIEW_LABELS[option]}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
